import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiActivity, FiHeart, FiSun, FiDroplet, FiEdit, FiShield } from 'react-icons/fi';
import client from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { toISODate, formatDate } from '../utils/dates';
import SymptomStep from './SymptomStep';
import MedicationStep from './MedicationStep';
import LifestyleStep from './LifestyleStep';
import PeriodStep from './PeriodStep';
import NotesStep from './NotesStep';
import RecoveryStep from './RecoveryStep';
import SectionCard from './SectionCard';
import DatePicker from '../components/DatePicker';
import {
  isSymptomsComplete,
  isMedicationComplete,
  isLifestyleComplete,
  isPeriodComplete,
  isNotesComplete,
  isRecoveryComplete,
} from './sectionCompletion';

export default function CheckInWizard() {
  const { id: editId } = useParams();
  const isEditMode = Boolean(editId);
  const { user } = useAuth();

  const [expandedSection, setExpandedSection] = useState(null);
  const [checkInType, setCheckInType] = useState('MORNING');
  const [checkInDate, setCheckInDate] = useState(toISODate(new Date()));
  const [submitting, setSubmitting] = useState(false);
  const [dateCheckIns, setDateCheckIns] = useState([]);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [editCheckInDate, setEditCheckInDate] = useState(null);
  const navigate = useNavigate();

  const [data, setData] = useState({
    painLevel: 1,
    painLocations: [],
    fatigueLevel: 1,
    moods: [],
    giSymptoms: [],
    penetrationPain: false,
    penetrationPainLevel: null,
    sleepQuality: 1,
    sleepHours: null,
    stressLevel: 1,
    recoveryPainLevel: 1,
    activityLevel: '',
    exerciseType: null,
    exerciseNotes: '',
    tookIbuprofen: false,
    tookMuscleRelaxant: false,
    isFlareDay: false,
    flareTime: null,
    isPeriodDay: false,
    cycleDay: null,
    periodFlow: null,
    crampIntensity: null,
    energyLevel: null,
    notes: '',
    medicationLogs: [],
    // Recovery fields
    recoverySelfAssessment: null,
    incisionObservations: [],
    incisionPainLevel: null,
    shoulderGasPainLevel: null,
    shoulderGasPainResolved: false,
    complications: [],
    complicationNotes: '',
    drainCatheterLogs: [],
  });

  // Fetch existing check-ins for the selected date (create mode only)
  useEffect(() => {
    if (isEditMode) return;
    client.get('/checkins', { params: { startDate: checkInDate, endDate: checkInDate } })
      .then(res => {
        setDateCheckIns(res.data);
        const hasMorning = res.data.some(c => c.checkInType === 'MORNING');
        const hasEvening = res.data.some(c => c.checkInType === 'EVENING');
        if (hasMorning && !hasEvening) setCheckInType('EVENING');
        else if (!hasMorning) setCheckInType('MORNING');
      }).catch(() => {});
  }, [isEditMode, checkInDate]);

  // Fetch existing check-in for editing
  useEffect(() => {
    if (!isEditMode) return;
    setLoadingEdit(true);
    client.get(`/checkins/${editId}`)
      .then(res => {
        const ci = res.data;
        setCheckInType(ci.checkInType);
        setEditCheckInDate(ci.checkInDate);
        setData({
          painLevel: ci.painLevel ?? 1,
          painLocations: ci.painLocations ?? [],
          fatigueLevel: ci.fatigueLevel ?? 1,
          moods: ci.moods ?? [],
          giSymptoms: ci.giSymptoms ?? [],
          penetrationPain: ci.penetrationPain ?? false,
          penetrationPainLevel: ci.penetrationPainLevel ?? null,
          sleepQuality: ci.sleepQuality ?? 1,
          sleepHours: ci.sleepHours ?? null,
          stressLevel: ci.stressLevel ?? 1,
          recoveryPainLevel: ci.recoveryPainLevel ?? 1,
          activityLevel: ci.activityLevel ?? '',
          exerciseType: ci.exerciseType ?? null,
          exerciseNotes: ci.exerciseNotes ?? '',
          tookIbuprofen: ci.tookIbuprofen ?? false,
          tookMuscleRelaxant: ci.tookMuscleRelaxant ?? false,
          isFlareDay: ci.isFlareDay ?? false,
          flareTime: ci.flareTime ?? null,
          isPeriodDay: ci.isPeriodDay ?? false,
          cycleDay: ci.cycleDay ?? null,
          periodFlow: ci.periodFlow ?? null,
          crampIntensity: ci.crampIntensity ?? null,
          energyLevel: ci.energyLevel ?? null,
          notes: ci.notes ?? '',
          medicationLogs: (ci.medicationLogs ?? []).map(ml => ({
            medicationId: ml.medicationId,
            taken: ml.taken ?? false,
          })),
          // Recovery fields
          recoverySelfAssessment: ci.recoverySelfAssessment ?? null,
          incisionObservations: ci.incisionObservations ?? [],
          incisionPainLevel: ci.incisionPainLevel ?? null,
          shoulderGasPainLevel: ci.shoulderGasPainLevel ?? null,
          shoulderGasPainResolved: ci.shoulderGasPainResolved ?? false,
          complications: ci.complications ?? [],
          complicationNotes: ci.complicationNotes ?? '',
          drainCatheterLogs: ci.drainCatheterLogs ?? [],
        });
      })
      .catch(() => {
        alert('Could not load check-in.');
        navigate('/history');
      })
      .finally(() => setLoadingEdit(false));
  }, [editId, isEditMode, navigate]);

  const update = (fields) => setData(prev => ({ ...prev, ...fields }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        checkInType,
        checkInDate: isEditMode ? editCheckInDate : checkInDate,
      };

      if (isEditMode) {
        await client.put(`/checkins/${editId}`, payload);
      } else {
        await client.post('/checkins', payload);
      }
      navigate(isEditMode ? '/history' : '/');
    } catch (err) {
      alert('Failed to save check-in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSection = (key) => {
    setExpandedSection(prev => prev === key ? null : key);
  };

  const hasMorning = dateCheckIns.some(c => c.checkInType === 'MORNING');
  const hasEvening = dateCheckIns.some(c => c.checkInType === 'EVENING');

  const sections = [
    { key: 'symptoms', title: 'Symptoms', icon: FiActivity, isComplete: isSymptomsComplete(data),
      content: <SymptomStep data={data} update={update} /> },
{ key: 'medication', title: 'Medication', icon: FiHeart, isComplete: isMedicationComplete(data),
      content: <MedicationStep data={data} update={update} checkInType={checkInType} /> },
    { key: 'lifestyle', title: 'Lifestyle', icon: FiSun, isComplete: isLifestyleComplete(data),
      content: <LifestyleStep data={data} update={update} checkInType={checkInType} /> },
    { key: 'period', title: 'Period', icon: FiDroplet, isComplete: isPeriodComplete(data),
      content: <PeriodStep data={data} update={update} /> },
    { key: 'notes', title: 'Notes', icon: FiEdit, isComplete: isNotesComplete(data),
      content: <NotesStep data={data} update={update} /> },
  ];

  if (user?.hasSurgeryProfile) {
    sections.push({
      key: 'recovery', title: 'Recovery', icon: FiShield, isComplete: isRecoveryComplete(data),
      content: <RecoveryStep data={data} update={update} />,
    });
  }

  if (loadingEdit) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:max-w-3xl lg:mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white drop-shadow-sm">
          {isEditMode ? 'Edit Check-in' : 'Daily Check-in'}
        </h1>
        {isEditMode && editCheckInDate ? (
          <p className="text-white/70 mt-1">
            {formatDate(editCheckInDate)} &mdash; {checkInType.charAt(0) + checkInType.slice(1).toLowerCase()}
          </p>
        ) : (
          <p className="text-white/70 mt-1">Tap a section to fill it in</p>
        )}
      </div>

      {/* Date picker and type selector (create mode only) */}
      {!isEditMode && (
        <>
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">Date</label>
            <DatePicker
              value={checkInDate}
              max={toISODate(new Date())}
              onChange={setCheckInDate}
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setCheckInType('MORNING')}
              disabled={hasMorning}
              className={`flex-1 py-3 rounded-xl font-medium transition
                ${checkInType === 'MORNING'
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'}
                ${hasMorning ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Morning {hasMorning && '(done)'}
            </button>
            <button
              type="button"
              onClick={() => setCheckInType('EVENING')}
              disabled={hasEvening}
              className={`flex-1 py-3 rounded-xl font-medium transition
                ${checkInType === 'EVENING'
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'}
                ${hasEvening ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Evening {hasEvening && '(done)'}
            </button>
          </div>
        </>
      )}

      {/* Section cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {sections.map(({ key, title, icon, isComplete, content }) => (
          <SectionCard
            key={key}
            title={title}
            icon={icon}
            isExpanded={expandedSection === key}
            isComplete={isComplete}
            onClick={() => toggleSection(key)}
          >
            {content}
          </SectionCard>
        ))}
      </div>

      {/* Submit button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="px-8 py-3 bg-white text-black rounded-xl font-medium hover:bg-white/90 disabled:opacity-50 transition"
        >
          {submitting ? 'Saving...' : isEditMode ? 'Update Check-in' : 'Save Check-in'}
        </button>
      </div>
    </div>
  );
}
